import {
  AdminInput,
  Button,
  DebtRepaymentPlan,
  DebtRepaymentService,
  Disclaimer,
  Div,
  Modal,
  P,
  Separator,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import {
  PLAN_NOTE_MAX,
  SERVICE_DEBT_NAMES,
  SERVICE_PLAN_TITLES,
  planMethodRows,
} from '../helpers/repaymentPlanLabels';

type Props = {
  service: DebtRepaymentService;
  /** The plan being stopped; null keeps the modal shut. */
  plan: DebtRepaymentPlan | null;
  /** What stays open once it stops. */
  repayableDebt: number;
  busy: boolean;
  onClose: () => void;
  onConfirm: (note: string | null) => Promise<boolean>;
};

/**
 * Stopping the active plan. Nothing is collected from then on and what was
 * collected stays repaid; the note is optional.
 */
export const StopRepaymentPlanModal = ({
  service,
  plan,
  repayableDebt,
  busy,
  onClose,
  onConfirm,
}: Props) => {
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!plan) setNote('');
  }, [plan]);

  const submit = async () => {
    const ok = await onConfirm(note.trim() || null);
    if (ok) onClose();
  };

  return (
    <Modal
      isOpen={!!plan}
      closeModal={onClose}
      logo={false}
      header={`Stop ${SERVICE_PLAN_TITLES[service]}`}
    >
      {plan && (
        <Div $flex $column $gap="16px">
          {/* A short list rather than centred chips: in a modal this narrow
              three chips wrapped two and one. */}
          <Div $box $p="16px" $flex $column $gap="8px">
            <Disclaimer $m="0" $left $color={lightTheme.lightGray}>
              Collecting from
            </Disclaimer>
            {planMethodRows(plan).map((row) => (
              <SummaryRow key={row.label} label={row.label} value={row.value} />
            ))}
            <Separator $m="4px 0" />
            <SummaryRow
              label="Repaid so far"
              value={displayCentsInDollars(plan.repaidAmount)}
              color={lightTheme.green}
            />
            <SummaryRow
              label="Left to repay"
              value={displayCentsInDollars(repayableDebt)}
              color={lightTheme.primary}
            />
          </Div>

          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            Nothing more is collected. What was already collected stays repaid,
            and the rest of the {SERVICE_DEBT_NAMES[service]} stays open.
          </Disclaimer>

          <AdminInput
            type="textarea"
            label="Note (optional)"
            placeholder="e.g. The owner paid the rest by check"
            value={note}
            maxLength={PLAN_NOTE_MAX}
            $textareaHeight={90}
            onChange={(event) => setNote(event.target.value)}
          />

          {/* Kept apart from the note: right under its counter the primary
              button read as part of the field. */}
          <Div $flex $gap="12px" $mt="16">
            <Button
              $selected
              $gray
              $w="calc(50% - 6px)"
              $wSM="calc(50% - 6px)"
              disabled={busy}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              $selected
              $w="calc(50% - 6px)"
              $wSM="calc(50% - 6px)"
              $loading={busy}
              disabled={busy}
              onClick={submit}
            >
              Stop plan
            </Button>
          </Div>
        </Div>
      )}
    </Modal>
  );
};

/** One "label ... value" line, value bold and coloured when it is money. */
const SummaryRow = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <Div $flex $alignCenter $between $gap="16px">
    <P $m="0">{label}</P>
    <P $m="0" $bold={!!color} $color={color}>
      {value}
    </P>
  </Div>
);
